/**
 * Zone route driver: the PA who gets this zone's biggest shift-2 route when the
 * build runs (BOXY-656). Separate from "Assigned Drivers", which only limits who
 * the planner may use.
 */

import { useState } from 'react';
import { Select } from '../ui';

interface ZoneRouteDriverProps {
  value?: string;
  drivers: { driver_uid: string; full_name: string }[];
  canEdit: boolean;
  saving?: boolean;
  error?: string | null;
  /** May return a promise: the picked value shows, locked, until it settles. */
  onChange: (driverUid: string | null) => Promise<void> | void;
}

export default function ZoneRouteDriver({ value, drivers, canEdit, saving, error, onChange }: ZoneRouteDriverProps) {
  // The stored value only changes after the zones reload, so hold the pick until then.
  const [picked, setPicked] = useState<{ uid: string | null } | null>(null);
  const busy = saving || picked !== null;
  const shown = picked ? picked.uid : value;
  const options = [
    { value: '', label: 'None' },
    ...[...drivers]
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map((d) => ({ value: d.driver_uid, label: d.full_name || d.driver_uid })),
  ];
  // The build still assigns a stored uid the driver list no longer has, so show it rather than "None".
  if (value && !drivers.some((d) => d.driver_uid === value)) {
    options.push({ value, label: `Unknown driver (${value})` });
  }

  return (
    <div>
      <label htmlFor="zone-route-driver" className="block text-sm font-medium text-gray-700 mb-1">
        Shift 2 route driver
      </label>
      <Select
        id="zone-route-driver"
        dir="auto"
        size="sm"
        options={options}
        value={shown ?? ''}
        disabled={!canEdit || busy}
        error={!!error}
        aria-busy={busy || undefined}
        onChange={async (e) => {
          const uid = e.target.value || null;
          setPicked({ uid });
          try {
            await onChange(uid);
          } finally {
            setPicked(null);
          }
        }}
      />
      {error ? (
        <p role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : (
        <p className="mt-1 text-xs text-gray-500">
          Gets this zone&apos;s biggest shift-2 route automatically. None = officer assigns.
        </p>
      )}
    </div>
  );
}
